/**
 * Dashboard SSI Scope ↔ sessionStorage (DEF-306).
 *
 * Portfolio Overview remounts on every sidebar navigation, so in-memory
 * `useState` alone drops company / building / view-as-user. Persist the full
 * scope for the tab lifetime (same pattern as Properties DEF-298). Never write
 * UserPreferences — a scope lens is ephemeral, not a saved preference.
 */

import type {
  DashboardScope,
  DashboardScopeMode,
} from '../components/DashboardScopeContext';

/** Session key — survives SPA navigations within the tab (DEF-306). */
export const DASHBOARD_SSI_SCOPE_SESSION_KEY = 'truspace.dashboard.ssiScope';

const SCOPE_MODES: ReadonlySet<DashboardScopeMode> = new Set([
  'none',
  'company',
  'building',
  'user',
]);

function isPositiveInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

/**
 * Persist the active SSI scope for restore on Dashboard remount.
 * Mode `none` clears the key instead of writing an empty payload.
 */
export function writeDashboardScopeSession(scope: DashboardScope): void {
  if (typeof sessionStorage === 'undefined') return;
  if (scope.mode === 'none') {
    sessionStorage.removeItem(DASHBOARD_SSI_SCOPE_SESSION_KEY);
    return;
  }
  sessionStorage.setItem(DASHBOARD_SSI_SCOPE_SESSION_KEY, JSON.stringify(scope));
}

/** Drop the persisted SSI scope (Clear / Exit). */
export function clearDashboardScopeSession(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(DASHBOARD_SSI_SCOPE_SESSION_KEY);
}

/**
 * Read last SSI scope from sessionStorage.
 * Invalid / incomplete payloads → `null` (caller falls back to no-scope).
 */
export function readDashboardScopeSession(): DashboardScope | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(DASHBOARD_SSI_SCOPE_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DashboardScope>;
    if (typeof parsed.mode !== 'string' || !SCOPE_MODES.has(parsed.mode)) {
      return null;
    }
    if (parsed.mode === 'none') return null;

    const companyId = parsed.companyId ?? null;
    const buildingId = parsed.buildingId ?? null;
    const scopeAsUserId = parsed.scopeAsUserId ?? null;

    if (companyId !== null && !isPositiveInt(companyId)) return null;
    if (buildingId !== null && !isPositiveInt(buildingId)) return null;
    if (scopeAsUserId !== null && !isPositiveInt(scopeAsUserId)) return null;

    if (
      !isNullableString(parsed.companyName)
      || !isNullableString(parsed.buildingName)
      || !isNullableString(parsed.scopeAsUserName)
      || !isNullableString(parsed.scopeAsUserEmail)
    ) {
      return null;
    }

    if (parsed.mode === 'company' && companyId == null) return null;
    if (parsed.mode === 'building' && buildingId == null) return null;
    if (parsed.mode === 'user' && scopeAsUserId == null) return null;

    return {
      mode: parsed.mode,
      companyId,
      companyName: parsed.companyName,
      buildingId,
      buildingName: parsed.buildingName,
      scopeAsUserId,
      scopeAsUserName: parsed.scopeAsUserName,
      scopeAsUserEmail: parsed.scopeAsUserEmail,
    };
  } catch {
    return null;
  }
}
